import { connect, disconnect, send, subscribe, IClient } from '@/plugins/socket';
import { VoidFn, ILogCallback, ILogType, IMigrateParams } from './types';
import { getStorage } from '@/utils/storage';

/**
 * socket 流程备注：
 *  1. 创建 websocket 客户端，并用这个客户端连接到 socket 服务器
 *    1.1 如果连接服务器成功，执行第 `2` 步
 *    1.2 如果连接服务器超时，则尝试重连服务器，共尝试 3 次，再次失败后，终止 socket 流程
 *    1.3 如果连接服务器异常，则直接终止 socket 流程
 *  2. 服务器连接成功后，同时执行以下流程(并行执行)
 *    2.1 开始发送 socket 请求，这个请求是告诉服务器: '需要创建一个迁移任务'
 *    2.2 开始订阅这个请求频道，用来接收服务器的请求响应
 *  3. 服务器接收到客户端发送的请求后，回应给客户端一个任务状态(包含 created，running, error)
 *    3.1 如果在限定的时间内接收到了服务器的响应，则执行以下流程：
 *      3.1.1 如果服务器给的响应信息是 created, 表示 '这个任务已经创建完毕，随时等待启动'
 *        3.1.1.1 客户端需要再次向服务器发送一个请求，这个请求是告诉服务器：'启动这个迁移任务'
 *        3.1.1.2 服务器收到 `3.1.1.1` 的请求后，不会再回应客户端了，此时客户端只需执行第 `4` 步的流程即可
 *      3.1.2 如果服务器给的响应信息是 running, 表示 '这个任务正在运行'，此时客户端只需执行第 `4` 步的流程即可
 *      3.1.3 如果服务器给的响应信息是 error, 表示 '这个任务已经完成'，此时终止 socket 流程
 *    3.2 如果在限定时间内，还是没接收到消息，则客户端可以判定为请求超时
 *      3.2.1 请求超时后，尝试重新发送请求，共尝试 5 次，请求的目的同第 `2.1` 步
 *      2.2.2 如果 5 次尝试后，还是没收到服务器的响应，则终止 socket 流程
 *  4. 接收到服务器的响应后，开始订阅相关消息频道(并行执行)
 *    4.1 开始订阅迁移日志频道
 *      4.1.1 如果遇到 '--end--' 字样的消息，则终止 socket 流程
 *      4.1.2 如果日志输出的时候，出现长时间的停顿，需要检测任务状态(有可能正在进行大文件拷贝，有可能断网)
 *        4.1.2.1 在限定时间内，没收到日志，则发送一个请求
 *        4.1.2.2 如果服务器有响应，则输出对应的提示信息
 *        4.1.2.3 如果没响应，则在下一次超时后，继续执行 `4.1.2.1`，直到 socket 流程被终止或有日志输出为止
 *    4.2 开始订阅服务器异常频道
 *      4.2.1 出现了异常消息，表示服务端代码运行出错了，此时终止 socket 流程
 */


/**
 * 数据迁入的日志 socket
 * 示例用法：
 *  const websocket = new MigrationSocket({ env: 'dev', migrationType: '', uuid: '', ... });
 *  const onMessage = (log: ILog) => { ... };
 *  websocket.test(onMessage);
 *  websocket.connect(onMessage);
 *  websocket.reconnect(onMessage);
 *  websocket.disconnect();
 */
export default class MigrationSocket {
  public reqCount = 0;                // 请求超时的重试次数
  public timeout = 5000;              // 接收订阅消息超时时间
  public userId: number;              // 当前登录用户的ID

  public requestUrl: string;          // 发送迁移请求的url
  public responseUrl: string;         // 订阅请求信息的url
  public migrationLogUrl: string;     // 订阅迁移日志的url
  public exceptionSysUrl: string;     // 订阅系统异常日志的url
  public exceptionUserUrl: string;    // 订阅用户异常日志的url

  public client!: IClient;            // websocket 客户端
  public params: IMigrateParams;      // 发送迁移请求的入参

  public printLog!: ILogCallback;     // 打印消息的回调函数
  public unsubscribeRequest?: VoidFn; // 取消订阅请求信息
  public unsubscribeMigrate?: VoidFn; // 取消订阅迁移日志
  public unsubscribeCheck?: VoidFn;   // 取消订阅任务状态检测
  public unsubscribeSysErr?: VoidFn;  // 取消订阅系统错误日志
  public unsubscribeUserErr?: VoidFn; // 取消订阅用户错误日志

  constructor(params: IMigrateParams) {
    const { uuid = '', migrationType = '' } = params;
    const useUUID = ['statictexturepaks', 'ies'].indexOf(migrationType) === -1; // 这两个迁移类型已经废弃(不需要 uuid 就可以进行迁移)

    this.params = params;
    this.userId = JSON.parse(getStorage('userInfo') || '{}').id;

    this.requestUrl = `/websocket/req/migrate/${migrationType}`;
    this.responseUrl = `/websocket/queue/${this.userId}/${uuid}`;
    this.migrationLogUrl = `/websocket/topic/${useUUID ? uuid : migrationType}`;
    this.exceptionSysUrl = '/websocket/topic/errors';
    this.exceptionUserUrl = `/websocket/queue/${this.userId}/errors`;
  }

  // 连接 socket 服务器(实例化时需手动调用)
  public connect(onMessage: ILogCallback) {
    this.printLog = onMessage;
    this.client = connect({
      retry: 3,
      timeout: this.timeout,
      url: '/websocket/fallback',
      onBefore: this.onConnectBefore.bind(this),
      onConnected: this.onConnectSuccess.bind(this),
      onError: this.onConnectError.bind(this),
    });
  }

  // 重连 socket 服务器(实例化时需手动调用)
  public reconnect(onMessage: ILogCallback) {
    this.printLog = onMessage;
    this.client = connect({
      retry: 3,
      timeout: this.timeout,
      url: '/websocket/fallback',
      onBefore: this.onConnectBefore.bind(this),
      onConnected: this.onReconnectSuccess.bind(this),
      onError: this.onConnectError.bind(this),
    });
  }

  // 测试 socket 连接是否通畅, 没其他作用(实例化时需手动调用)
  public test(onMessage: ILogCallback) {
    this.printLog = onMessage;
    this.client = connect({
      debug: true,
      url: '/websocket/fallback',
      onBefore: this.onConnectBefore.bind(this),
      onConnected: this.subscribeTest.bind(this),
      onError: this.onConnectError.bind(this),
    });
  }

  // 断开 socket 连接(需通过实例方法手动调用)
  public disconnect() {
    this.unsubscribeRequest?.();
    this.unsubscribeMigrate?.();
    this.unsubscribeCheck?.();
    this.unsubscribeSysErr?.();
    this.unsubscribeUserErr?.();
    disconnect(this.client);
  }

  // 连接 socket 之前
  public onConnectBefore(reconnectCount: number) {
    if (reconnectCount < 1) {
      // 新建连接
      this.printLog({ type: 'debug', text: '开始连接服务器...' });
      this.printLog({ type: 'debug', text: 'start connect: /websocket/fallback' });
    } else {
      // 重新连接
      this.printLog({ type: 'tips', text: '服务器连接超时，正在尝试重新连接服务器...' });
      this.printLog({ type: 'debug', text: 'try reconnect: /websocket/fallback' });
    }
  }

  // 连接 socket 成功
  public onConnectSuccess() {
    // 如果是新开的任务，则需要先发送迁移请求并收到对应的响应信息后，才开始订阅迁移日志和服务器异常日志
    this.printLog({ type: 'debug', text: '服务器连接成功...' });
    this.printLog({ type: 'debug', text: 'connect success: /websocket/fallback' });

    // 订阅请求频道(主要接收 onRequestBefore 里 send 请求的响应信息)
    this.unsubscribeRequest = subscribe({
      client: this.client,
      timeout: this.timeout,
      url: this.responseUrl,
      onBefore: this.onRequestBefore.bind(this),
      onMessage: this.onRequestMessage.bind(this),
      onTimeout: this.onRequestTimeout.bind(this),
    });
  }

  // 重连 socket 成功
  public onReconnectSuccess() {
    // 如果是重连，则不发请求，直接订阅迁移日志和服务器异常日志
    this.printLog({ type: 'debug', text: '服务器连接成功...' });
    this.printLog({ type: 'debug', text: 'connect success: /websocket/fallback' });
    this.subscribeMigration();
    this.subscribeException();
  }

  // 连接 socket 失败
  public onConnectError(error: string) {
    this.printLog({ type: 'debug', text: '服务器连接失败...' });
    this.printLog({ type: 'exception', text: error });
  }

  // 接收请求的响应信息之前
  public onRequestBefore() {
    // 第一次请求是告诉服务器：要创建任务
    this.printLog({ type: 'debug', text: '开始请求创建任务...' });
    this.printLog({ type: 'debug', text: `start send: ${this.requestUrl} - reqMig` });
    send(this.client, this.requestUrl, { ...this.params, messageType: 'reqMig' });
  }

  // 接收请求的响应信息后
  public onRequestMessage(message: string) {
    const reqUUID = this.params.uuid;
    const { uuid: resUUID, taskStatus } = JSON.parse(message || '{}') as any;
    this.reqCount = 0;  // 只要收到消息，就重新计算超时请求次数

    // 不是当前任务的订阅消息(后续的消息可能就是当前任务的消息，所以这里不能取消订阅)
    if (resUUID !== reqUUID) {
      return;
    }

    switch (taskStatus) {
      case 'created':
        // 任务创建完成
        this.printLog({ type: 'debug', text: '服务器已接收请求...' });
        this.printLog({ type: 'debug', text: `response success: ${this.responseUrl} - created` });
        this.printLog({ type: 'debug', text: '开始请求启动任务...' });
        this.printLog({ type: 'debug', text: `start send: ${this.requestUrl} - clientReady` });

        // 第二次请求是告诉服务器：启动并执行迁移任务(这一次的请求服务端不会响应任何消息)
        const secondReqParams = { ...this.params, messageType: 'clientReady' };
        send(this.client, this.requestUrl, secondReqParams);

        // 开始订阅相关日志，并取消订阅当前的请求频道
        // 由于已经收到了响应信息，这个频道已经无用，所以需要取消订阅
        // 虽然后续的'检测任务状态'的请求频道和当前频道是一样的，但是超时的业务逻辑不一样
        // 所以不能重复利用这个订阅，而是要重新开启一个相同的订阅
        this.subscribeMigration();
        this.subscribeException();
        this.unsubscribeRequest!();
        break;
      case 'running':
        // 任务正在运行
        this.printLog({ type: 'tips', text: '任务正在运行中，请耐心等待...' });
        this.printLog({ type: 'debug', text: `response success: ${this.responseUrl} - running` });

        // 开始订阅相关日志，并并取消订阅当前的请求频道
        // 理由同上
        this.subscribeMigration();
        this.subscribeException();
        this.unsubscribeRequest!();
        break;
      case 'error':
        // 任务出错或已经结束
        this.printLog({ type: 'tips', text: '任务已经结束' });
        this.printLog({ type: 'debug', text: `response success: ${this.responseUrl} - error` });
        this.disconnect();
        break;
    }
  }

  // 接收请求的响应信息超时
  public onRequestTimeout() {
    if (this.reqCount < 5) {
      // 如果迟迟收不到任务状态信息，则重新发送请求
      this.reqCount++;
      this.printLog({ type: 'tips', text: '请求超时，正在尝试重新发送请求...' });
      this.printLog({ type: 'debug', text: `start send: ${this.requestUrl} - reqMig` });
      send(this.client, this.requestUrl, { ...this.params, messageType: 'reqMig' });
    } else {
      // 发送了5次请求后，还是超时，则断开连接
      this.printLog({ type: 'tips', text: '服务器无响应，已断开连接' });
      this.printLog({ type: 'debug', text: `response fail: ${this.responseUrl}` });
      this.reqCount = 0;
      this.disconnect();
    }
  }

  // 订阅迁移日志及状态检测频道
  public subscribeMigration() {
    // 订阅迁移日志
    this.unsubscribeMigrate = subscribe({
      client: this.client,
      timeout: this.timeout,
      url: this.migrationLogUrl,
      onBefore: this.onMigrationBefore.bind(this),
      onMessage: this.onMigrationMessage.bind(this),
      onTimeout: this.onMigrationTimeout.bind(this),
    });

    // 订阅任务状态检测请求(主要接收 onMigrationTimeout 里 send 请求的响应信息)
    // 在接收迁移日志超时的时候，会不停发送检测任务状态的请求，直到有迁移日志出来才停止
    // 至于为什么要重新开启一个请求订阅频道，理由详见 this.onRequestMessage 里的注释
    this.unsubscribeCheck = subscribe({
      client: this.client,
      url: this.responseUrl,
      onBefore: this.onCheckBefore.bind(this),
      onMessage: this.onCheckMessage.bind(this),
    });
  }

  // 订阅迁移消息之前
  public onMigrationBefore() {
    this.printLog({ type: 'debug', text: '开始订阅迁移日志...' });
    this.printLog({ type: 'debug', text: `start subscribe: ${this.migrationLogUrl}` });
  }

  // 接收迁移消息时
  public onMigrationMessage(message: string) {
    const match = message.match(/^\[[^\]]*]/) || []; // 查找字符串里以 [xxx] 开头的字符串
    const type = (match[0] || '').slice(1, -1).toLowerCase() as ILogType;
    this.printLog({ type, text: message });

    // 当出现 end 消息时，断开 socket 连接
    if (message === '[INFO]--END--') {
      this.disconnect();
    }
  }

  // 接收迁移消息超时
  public onMigrationTimeout() {
    // 发送请求，用来检测任务状态
    this.printLog({ type: 'tips', text: '正在检查任务状态...' });
    this.printLog({ type: 'debug', text: `start send: ${this.requestUrl} - reqMig` });
    send(this.client, this.requestUrl, { ...this.params, messageType: 'reqMig' });
  }

  // 订阅任务状态检测频道之前
  public onCheckBefore() {
    this.printLog({ type: 'debug', text: '开始订阅状态检测日志...' });
    this.printLog({ type: 'debug', text: `start subscribe: ${this.responseUrl}` });
  }

  // 接收任务检测状态时
  public onCheckMessage(message: string) {
    const reqUUID = this.params.uuid;
    const { uuid: resUUID, taskStatus } = JSON.parse(message || '{}') as any;
    this.reqCount = 0;  // 只要收到消息，就重新计算超时请求次数

    // 不是当前任务的订阅消息
    if (resUUID !== reqUUID) {
      return;
    }

    switch (taskStatus) {
      case 'created':
        // 任务创建完成(一般检测的时候，不会出现这个状态，因为是在任务启动之后，才开始检测的)
        this.printLog({ type: 'debug', text: '任务已创建...' });
        this.printLog({ type: 'debug', text: `response success: ${this.responseUrl} - created` });
        break;
      case 'running':
        // 任务正在运行
        this.printLog({ type: 'tips', text: '正在后台运行耗时任务，请耐心等待...' });
        this.printLog({ type: 'debug', text: `response success: ${this.responseUrl} - running` });
        break;
      case 'error':
        // 任务出错或已经结束
        this.printLog({ type: 'tips', text: '任务已经结束' });
        this.printLog({ type: 'debug', text: `response success: ${this.responseUrl} - error` });
        this.disconnect();
        break;
    }
  }

  // 订阅异常频道
  public subscribeException() {
    // 订阅系统错误频道
    this.unsubscribeSysErr = subscribe({
      client: this.client,
      url: this.exceptionSysUrl,
      onBefore: () => {
        this.printLog({ type: 'debug', text: '开始订阅系统异常日志...' });
        this.printLog({ type: 'debug', text: 'start subscribe: /websocket/topic/errors' });
      },
      onMessage: (message) => {
        this.printLog({ type: 'exception', text: this.exceptionHandler(message) });
      },
    });

    // 订阅用户错误频道
    this.unsubscribeUserErr = subscribe({
      client: this.client,
      url: this.exceptionUserUrl,
      onBefore: () => {
        this.printLog({ type: 'debug', text: '开始订阅用户异常日志...' });
        this.printLog({ type: 'debug', text: `start subscribe: /websocket/queue/${this.userId}/errors` });
      },
      onMessage: (message) => {
        this.printLog({ type: 'exception', text: this.exceptionHandler(message) });
      },
    });
  }

  // 订阅测试频道
  public subscribeTest() {
    const unsubscribeTest = subscribe({
      client: this.client,
      timeout: 1000 * 20,
      url: '/websocket/topic/greetings',
      onBefore: () => {
        this.printLog({ type: 'debug', text: '开始发送测试请求...' });
        this.printLog({ type: 'debug', text: `start send: /websocket/req/greetings` });
        send(this.client, '/websocket/req/greetings', 'test');

        this.printLog({ type: 'debug', text: '开始订阅测试日志...' });
        this.printLog({ type: 'debug', text: `start subscribe: /websocket/topic/greetings` });
      },
      onMessage: (message) => {
        this.printLog({ type: 'info', text: message });
      },
      onTimeout: () => {
        // 超时后立即断开连接
        unsubscribeTest();
        disconnect(this.client);
      },
    });
  }

  // 异常信息处理
  public exceptionHandler(error: string) {
    const { message = '', rtnCode = '' } = JSON.parse(error || '{}');
    return rtnCode ? `${message}。错误码：${rtnCode}` : error;
  }
}
